import DayJS from 'dayjs';

const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Naming convention: a `day` is a `YYYY-MM-DD` string, a `date` is a `Date` (with time).
 *   toUtcDay:              (date: Date): string      // → day
 *   shiftUtcDay:           (day: string, …): string  // → day, from day
 *   shiftUtcDayFromDate:   (date: Date, …): string   // → day, from date
 *   startOfUtcDateFromDay: (day: string): Date       // → date, from day
 *   startOfUtcDate:        (date: Date): Date        // → date, from date
 */
const DateHelpers = {
    toUtcDay: (date: Date): string => date.toISOString().slice(0, 10),

    shiftUtcDay: (day: string, deltaDays: number): string => {
        const date = new Date(`${day}T00:00:00.000Z`);
        date.setUTCDate(date.getUTCDate() + deltaDays);
        return DateHelpers.toUtcDay(date);
    },

    shiftUtcDayFromDate: (date: Date, deltaDays: number): string =>
        DateHelpers.shiftUtcDay(DateHelpers.toUtcDay(date), deltaDays),

    startOfUtcDateFromDay: (day: string): Date => new Date(`${day}T00:00:00.000Z`),

    startOfUtcDate: (date: Date): Date => DateHelpers.startOfUtcDateFromDay(DateHelpers.toUtcDay(date)),

    toUtcWeekDays: (date: Date, weeksAgo = 0): string[] => {
        const daysSinceMondayByUtcDay = [6, 0, 1, 2, 3, 4, 5];
        const daysSinceMonday = daysSinceMondayByUtcDay[date.getUTCDay()];

        const daysPerWeek = 7;
        const daysBackToMonday = daysSinceMonday + weeksAgo * daysPerWeek;
        const monday = DateHelpers.shiftUtcDayFromDate(date, -daysBackToMonday);
        const daysAfterMonday = [0, 1, 2, 3, 4, 5, 6];

        return daysAfterMonday.map((dayCount) => DateHelpers.shiftUtcDay(monday, dayCount));
    },

    toLastUtcDays: (date: Date, count: number): string[] =>
        Array.from({ length: count }, (_, index) => DateHelpers.shiftUtcDayFromDate(date, index - (count - 1))),

    countUtcDaysBetween: (fromDay: string, toDay: string): number =>
        Math.round(
            (DateHelpers.startOfUtcDateFromDay(toDay).getTime() -
                DateHelpers.startOfUtcDateFromDay(fromDay).getTime()) /
                MILLISECONDS_PER_DAY,
        ),

    toStartOfDayISO: (date: string): string => {
        return /^\d{4}-\d{2}-\d{2}$/.test(date) ? `${date}T00:00:00.000Z` : date;
    },
    toEndOfDayISO: (date: string): string => {
        return /^\d{4}-\d{2}-\d{2}$/.test(date) ? `${date}T23:59:59.999Z` : date;
    },

    getNextMonthSameDay: (date: Date = new Date()): Date => DayJS(date).add(1, 'month').toDate(),

    getOneYearFromNow: (date: Date = new Date()): Date => DayJS(date).add(1, 'year').toDate(),

    todayIsSameDayOfMonthAsAnchor: (anchorDate: Date): boolean => {
        const today = DayJS();
        const daysInThisMonth = today.daysInMonth();

        const anchor = DayJS(anchorDate);
        const anchorDay = anchor.date();

        if (anchorDay > daysInThisMonth) {
            return today.date() === daysInThisMonth;
        }

        return today.date() === anchorDay;
    },

    isSameDayOfYear: (a: Date, b: Date): boolean => {
        const d1 = DayJS(a);
        const d2 = DayJS(b);

        return d1.month() === d2.month() && d1.date() === d2.date();
    },

    formatDateTime: (date: string | Date, timeZone = 'UTC'): string =>
        new Intl.DateTimeFormat('en-US', {
            timeZone,
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            timeZoneName: 'short',
        }).format(new Date(date)),

    formatSeconds: (value: number, precision = 4) => {
        const abs = Math.abs(value);

        if (abs < 1) {
            const ms = value * 1000;
            const factor = Math.pow(10, precision);
            const rounded = Math.round(ms * factor) / factor;

            return `${rounded}ms`;
        }

        const factor = Math.pow(10, precision);
        const rounded = Math.round(value * factor) / factor;

        return `${rounded}s`;
    },

    dayLabel: (day: string): string => {
        const date = DayJS(day);

        if (date.isSame(DayJS(), 'day')) {
            return 'Today';
        }

        if (date.isSame(DayJS().subtract(1, 'day'), 'day')) {
            return 'Yesterday';
        }

        return date.format('dddd, MMMM D');
    },
};

export { DateHelpers };
